// Zustand store for session state management

import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import type {
  Branch,
  DeckEntry,
  SessionLobbyState,
  SessionResultsEvent,
} from '@dinder/shared/types';
import type { Participant } from '../types';
import { useOrderStore } from './orderStore';
import { clearRejoinToken } from '../services/rejoinToken';

interface Location {
  latitude: number;
  longitude: number;
  address?: string;
}

interface SessionState {
  // Session data
  sessionCode: string | null;
  rejectedSessionCode: string | null;
  participants: Participant[];
  currentUserId: string | null;
  /** The Session's Branch, from the join ack. Undefined before the entry fork. */
  branch?: Branch;

  lobby?: SessionLobbyState;
  setLobby: (lobby?: SessionLobbyState) => void;

  // Location data
  location?: Location;
  searchRadiusMiles?: number;
  restaurants: DeckEntry[];

  // Selection data
  selections: string[]; // Current user's Place IDs
  // How far through the Deck this Participant has swiped. Persisted alongside
  // the Selections it was built from (#404), so a reload resumes at the next
  // undecided card rather than making every decided card decidable again.
  deckCursor: number;
  allSelections: Record<string, string[]>; // All participants' selections (after reveal)
  liveSelections: Record<string, string[]>; // placeId -> displayNames who live-selected it (remote only)
  // Deck Entries whose Full House already took this phone's screen over this
  // round. Kept here, not in the Deck: the reconnect gate and a reload unmount
  // the Deck, and a larger roster completing the same entry must not
  // celebrate it twice (#513).
  fullHousesShown: string[];
  restaurantNames: Record<string, string>; // placeId -> name mapping for display
  overlappingOptions: DeckEntry[];
  topPick?: { restaurant: DeckEntry; likedBy: number; of: number };
  /** The Shopping List the Cook ending minted, as the id of its own URL (#262). */
  shoppingListId?: string;

  // The crowned placeId a Group Order was opened for (set when "Order
  // together" is tapped). Persisted so a hard reload of /order can re-fire
  // order:open with no other source for the placeId (the route carries only
  // the session code). Cleared by resetSelections/resetSession.
  orderPlaceId: string | null;

  // Session status
  sessionStatus: 'waiting' | 'selecting' | 'complete' | 'expired';
  isConnected: boolean;
  /**
   * ISO timestamp of the Session's expiry, as last read from GET /api/sessions.
   * The backend slides the TTL forward on a join, a submission, a Restart and
   * the Group Order writes, and no socket event carries the new value — so the
   * pages that fetch the Session re-read it on every roster change.
   */
  expiresAt: string | null;

  // Actions
  setSessionCode: (code: string) => void;
  setBranch: (branch?: Branch) => void;
  setCurrentUserId: (userId: string) => void;
  addParticipant: (participant: Participant) => void;
  removeParticipant: (participantId: string) => void;
  updateParticipants: (participants: Participant[]) => void;

  // Selection actions
  addSelection: (placeId: string) => void;
  removeSelection: (placeId: string) => void;
  recordLiveSelection: (placeId: string, displayName: string) => void;
  retractLiveSelection: (placeId: string, displayName: string) => void;
  markFullHouseShown: (placeId: string) => void;
  setDeckCursor: (index: number) => void;

  // Results actions
  setResults: (results: SessionResultsEvent) => void;

  // Group Order actions
  setOrderPlaceId: (placeId: string | null) => void;

  // Status actions
  setSessionStatus: (status: 'waiting' | 'selecting' | 'complete' | 'expired') => void;
  setConnectionStatus: (isConnected: boolean) => void;
  setExpiresAt: (expiresAt: string) => void;

  resetSession: () => void;
  resetSelections: () => void;
}

const emptyRound = {
  restaurants: [],
  selections: [],
  deckCursor: 0,
  allSelections: {},
  liveSelections: {},
  fullHousesShown: [],
  restaurantNames: {},
  overlappingOptions: [],
  topPick: undefined,
  shoppingListId: undefined,
  orderPlaceId: null,
};

function discardCredential(state: SessionState) {
  const me = state.participants.find((p) => p.participantId === state.currentUserId);
  if (state.sessionCode && me) {
    void clearRejoinToken(state.sessionCode, me.displayName).catch(() =>
      console.warn('Saved session credentials could not be cleared.')
    );
  }
}

const initialState = {
  ...emptyRound,
  sessionCode: null,
  rejectedSessionCode: null,
  participants: [],
  currentUserId: null,
  branch: undefined,
  lobby: undefined,
  location: undefined,
  searchRadiusMiles: undefined,
  sessionStatus: 'waiting' as const,
  isConnected: false,
  expiresAt: null,
};

export const useSessionStore = create<SessionState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setLobby: (lobby) =>
          set((state) => {
            if (!lobby) return { lobby: undefined };
            if (
              lobby.sessionCode !== state.sessionCode ||
              (state.lobby?.sessionCode === lobby.sessionCode &&
                (lobby.revision < state.lobby.revision ||
                  (lobby.revision === state.lobby.revision &&
                    state.lobby.state === 'complete' &&
                    lobby.state === 'selecting')))
            )
              return state;
            // Rejoin and HTTP hydration can recover a Restart whose broadcast
            // this phone missed, even after the next Deck has already started.
            // A waiting snapshot has no previous outcome even when its round
            // number already matches.
            const discardRound =
              lobby.state === 'waiting' ||
              (state.lobby && lobby.round !== undefined && lobby.round !== state.lobby.round);
            if (discardRound) useOrderStore.getState().clear();
            return {
              ...(discardRound ? emptyRound : {}),
              lobby,
              branch: lobby.branch,
              sessionStatus: lobby.state,
              location: lobby.location,
              searchRadiusMiles: lobby.searchRadiusMiles,
              participants: lobby.participants.map((participant) => ({
                ...participant,
                sessionCode: lobby.sessionCode,
              })),
            };
          }),

        // Session actions
        setSessionCode: (code) => set({ sessionCode: code }),

        setBranch: (branch) => set({ branch }),

        setCurrentUserId: (userId) => set({ currentUserId: userId }),

        addParticipant: (participant) =>
          set((state) => ({
            participants: [...state.participants, participant],
          })),

        removeParticipant: (participantId) =>
          set((state) => ({
            participants: state.participants.filter((p) => p.participantId !== participantId),
          })),

        updateParticipants: (participants) => set({ participants }),

        // Selection actions
        addSelection: (placeId) =>
          set((state) => {
            if (state.selections.includes(placeId)) {
              return state; // Already selected, no change
            }
            return { selections: [...state.selections, placeId] };
          }),

        removeSelection: (placeId) =>
          set((state) => ({
            selections: state.selections.filter((id) => id !== placeId),
          })),

        // Keyed by displayName, not participantId: participantId IS socket.id and
        // is re-minted on every reconnect, so a reloaded Participant replaying
        // their whole deck would otherwise be counted as several humans.
        // See ADR 0009.
        recordLiveSelection: (placeId, displayName) =>
          set((state) => {
            const names = state.liveSelections[placeId] ?? [];
            if (names.includes(displayName)) return state;
            return {
              liveSelections: { ...state.liveSelections, [placeId]: [...names, displayName] },
            };
          }),

        // The Undo mirror (#410): the sender took the Live Selection back, so
        // stop counting it. Same displayName key, so a retraction lands on the
        // entry a rejoin collapsed onto.
        retractLiveSelection: (placeId, displayName) =>
          set((state) => {
            const names = state.liveSelections[placeId] ?? [];
            if (!names.includes(displayName)) return state;
            return {
              liveSelections: {
                ...state.liveSelections,
                [placeId]: names.filter((n) => n !== displayName),
              },
            };
          }),
        markFullHouseShown: (placeId) =>
          set((state) =>
            state.fullHousesShown.includes(placeId)
              ? state
              : { fullHousesShown: [...state.fullHousesShown, placeId] }
          ),
        setDeckCursor: (index) => set({ deckCursor: index }),

        // Results actions
        setResults: (results) =>
          set((state) => ({
            lobby: state.lobby ? { ...state.lobby, state: 'complete' } : undefined,
            allSelections: results.allSelections,
            restaurantNames: results.restaurantNames,
            overlappingOptions: results.overlappingOptions,
            topPick: results.topPick,
            shoppingListId: results.shoppingListId,
            sessionStatus: 'complete',
          })),

        setOrderPlaceId: (placeId) => set({ orderPlaceId: placeId }),

        // Status actions
        setSessionStatus: (status) => {
          if (status === 'expired') discardCredential(get());
          set({ sessionStatus: status });
        },

        setConnectionStatus: (isConnected) => set({ isConnected }),

        setExpiresAt: (expiresAt) => set({ expiresAt }),

        resetSession: () => {
          discardCredential(get());
          useOrderStore.getState().clear();
          set(initialState);
        },

        resetSelections: () => {
          // A Restart voids the Match, and the server DELs both order keys in
          // the same resetForRestart pipeline — never render last venue's basket.
          useOrderStore.getState().clear();
          set((state) => ({
            ...emptyRound,
            // The server's resetForRestart clears every Participant's
            // Submission; a stale true here would re-seed a fresh Deck as
            // already submitted and mis-count "x of y have swiped".
            participants: state.participants.map((p) => ({ ...p, hasSubmitted: false })),
            sessionStatus: 'selecting',
          }));
        },
      }),
      {
        name: 'dinner-session-storage',
        version: 1,
        // #304: identity is per-tab. localStorage is origin-wide, so a second
        // tab of the same browser rehydrated the first tab's Participant and
        // its auto-rejoin evicted the host. sessionStorage is scoped to the
        // tab and survives reload (and iOS background-and-return), which is
        // exactly the case this persistence exists for.
        storage: createJSONStorage(() => sessionStorage),
        // isConnected is live socket state; rehydrating it as true would lie.
        partialize: ({ isConnected: _isConnected, ...rest }: SessionState): Partial<SessionState> =>
          rest,
        // Pre-v1 blobs have unversioned, possibly stale shapes — discard them.
        migrate: () => ({ ...initialState }),
        merge: (persisted, current) => ({
          ...current,
          ...((persisted ?? {}) as Partial<SessionState>),
          isConnected: false,
        }),
      }
    ),
    { name: 'DinnerSession' }
  )
);
